export const sendSmsDocumentationMarkdown = `
> ⚠️ **HIPAA Security Notice**
> 
> SMS communications are not encrypted or secure. DO NOT configure workflows that request, collect, or transmit PHI via SMS.
>
> PHI includes: patient names, medical conditions, symptoms, medications, appointment details with providers, or any health-related information.

### What does this block do?
The Send SMS Block sends a text message to the recipient's phone number. It allows you to configure the message content and defines a timeout duration—after which the block will fail if the message is not sent. This block is integrated into workflow automation systems to enable timely alerts and notifications via SMS.

### When to use it?
- Use this block when you need to send appointment reminders, alerts, or other notifications to patients or staff.
- Employ it in cases where immediate communication is required to ensure patients are informed about critical updates.
- Integrate it in workflows where a quick message delivery confirmation (or failure) is needed before proceeding to the next steps.

### Example flows
1. **Appointment Reminder Notification:**

    After a patient's appointment is scheduled, the flow triggers the Send SMS Block to deliver a reminder message with the appointment details. If the SMS fails to send within the specified timeout, the flow can trigger an alternative alert.

2. **Post-Visit Follow-Up:**

    Once a patient visit is completed, the flow uses this block to send a follow-up message thanking the patient and providing additional instructions or feedback requests.

3. **Emergency Alert:**

    In a critical situation, the system can send an urgent SMS to notify the patient immediately about necessary actions or changes in the schedule.

### How to configure this block
1. **Input the Phone Number to send the SMS to:**
    - Enter the recipient's phone number in this required field.
    - Example: "+14155551234" (US) or "+14165551234" (Canada).
    - The system validates the format to ensure it is a valid US or Canadian phone number in E.164 format.
    - Canadian recipients require an SMS-capable sender selected in the Sender Phone Number field below — the default short code can only send to US numbers.

2. **Construct the Message:**
    - In the Message text area, enter the content of the SMS.
    - Default message: "Hello, this is an automated text from your healthcare provider." Customize as needed.
    - You can insert dynamic variables from upstream blocks as needed to create the message for things like patient name, appointment details, etc.
    - SMS messages are limited to 160 GSM characters per segment (70 characters if the message contains emoji, curly quotes, or other non-GSM characters). Longer messages are split into segments (153/67 characters per segment when concatenated), which may incur additional costs.
    - We recommend keeping messages under 320 characters. Longer messages split into more segments than some carriers accept, and the carrier rejects the entire message with Twilio error 30019 ("Content size exceeds carrier limit").
    - Because non-GSM characters reduce the per-segment limit to 70/67, a message that looks short can still exceed a carrier's segment cap.

3. **Timeout:**
    - Enter the number "15" to set the timeout duration.
    - **Units Dropdown:**
        - Choose "Minutes" (default is Minutes). Other options such as "Hours" or "Seconds" are available as needed.

4. **Test Mode:**
    - Use the Test Mode toggle to simulate sending an SMS without actually transmitting a live message.

5. **Mock output for test run:**
    - Toggle the "Mock output for test run" switch (default is Off) to simulate output data and test downstream integration.

### How to test
1. **Enable Test Mode or Mock Output:**
    - Begin by toggling either Test Mode or Mock output for test run to simulate the SMS sending process.

2. **Run a Test Flow:**
    - Execute your flow with test data to ensure that the block processes the SMS configuration correctly.

3. **Verify the Results:**
    - Check that the SMS is sent (or simulated) and that the output returns details such as success status and send timestamp.

4. **Review Timeout Behavior:**
    - Confirm that the block fails or returns an error if the SMS is not sent within the specified timeout duration.

### Error Conditions
- **NO_VALID_PHONE_NUMBERS Error**: This error occurs when the system cannot find any valid phone numbers for the recipient. Common causes include:
  - **Recent SMS Failures**: System blocks phone numbers that had failed SMS delivery attempts within the past 30 days (we have logic in place to prevent sending SMS to landlines or repeatedly failing numbers).
  - **No Phone Numbers**: Recipient has no phone numbers recorded in the system.
  - **All Numbers Unsubscribed/Inactive**: Recipient has unsubscribed from all phone numbers or all numbers are marked as inactive.
  - **Invalid Number Types**: All numbers are of types not allowed for SMS (only Cell, Work, and Home numbers are supported).
- **Rate Limiting**: Too many SMS requests in a short period may trigger rate limiting. If you are testing sending SMS messages to yourself for example, you may run into the limit of only being able to receive 24 messages per day.
  - For campaigns that are sending to a large number of people, there is a limit of 3 messages per second. We'd recommend spreading your load out over time to avoid hitting this limit.
- **Content Size Exceeds Carrier Limit (Twilio error 30019)**: The message was too long for the recipient's carrier to accept, so none of it was delivered. Shorten the message body, and check whether dynamic variables from upstream blocks are expanding it past the limit at runtime.

### Best practices
  - **Validate Phone Number Format**:
    - Always ensure the recipient's phone number includes the proper country code (e.g., "+1") and matches one of the accepted formats to prevent messaging failures.
  - **Keep Messages Concise**:
    - SMS messages have character limitations; keep the content brief and to the point.
    - Stay under 320 characters. Past that point messages cost more, read poorly on the handset, and risk carrier rejection.
  - **Set Reasonable Timeout Settings**:
    - Choose a timeout value that balances prompt notification with network delays—15 minutes is a typical starting point.
  - **Test Thoroughly**:
    - Utilize Test Mode and Mock output for test runs to verify the configuration before deploying the flow in a live environment.
  - **Monitor and Review**:
    - After deployment, monitor your flow to ensure messages are delivered as expected and adjust settings if necessary.
  - **Respect Opt-Out Requests**:
    - Ensure your SMS system respects recipient opt-out requests to comply with regulations.

### How to get more help
- **TCPA**: [TCPA Compliance](https://www.fdic.gov/regulations/compliance/manual/8/viii-5.1.pdf)
- **SMS deliverability**: [SMS deliverability](https://www.twilio.com/docs/glossary/what-is-sms-delivery-deliverability)
- **Debugging**: Try sending a test message to yourself to see if it is being delivered. If it is not, you may need to check your phone number or the phone number of the person you are sending the message to.

### Example Output


~~~json
{
    "to": "12345678910",
    "sid": "SM00000000000000000000000000000000",
    "uri": "/2010-04-01/Accounts/AC00000000000000000000000000000000/Messages/SM00000000000000000000000000000000.json",
    "body": "Hello, this is an test text.",
    "from": "75442",
    "price": "0.002",
    "status": "delivered",
    "dateSent": "2025-03-27T13:16:33.515Z",
    "numMedia": "0",
    "timedOut": false,
    "direction": "outbound-api",
    "errorCode": 0,
    "priceUnit": "USD",
    "accountSid": "AC00000000000000000000000000000000",
    "apiVersion": "2010-04-01",
    "dateCreated": "2025-03-27T13:16:33.515Z",
    "dateUpdated": "2025-03-27T13:16:33.515Z",
    "numSegments": "1",
    "errorMessage": "",
    "subresourceUris": {
        "media": "/2010-04-01/Accounts/AC00000000000000000000000000000000/Messages/SM00000000000000000000000000000000/Media.json"
    },
    "messagingServiceSid": "MG00000000000000000000000000000000"
}
~~~
`;
